import React from "react";
import get from "lodash/get";
import defaults from "lodash/defaults";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import NavigationComponent from "../../athlete-portal/calendar/components/NavigationComponent";
import { ReportsBackHandler } from "../../athlete-portal/calendar/components/ReportsBackHandler";
import SvgIcon from "../../../parts/SvgIcon";
import {
  NumberLiftsIcon,
  OverUnderBaselineIcon,
  PersonalRecordTableIcon,
  PrimarySecondaryVolumeSplitIcon,
  RepMaxIcon,
  RPECustomIcon,
  VolumeIntensityIcon,
  VolumeSplitCoreLiftIcon,
} from "./IconComponents";
import { RillContext } from "../../../rill/RillReact/share/base";
import { isPushOrPerformClient } from "../../../utils/user-helpers";

const getMetricsBlocks = ({ intensityMeasure, t }) => [
  {
    name: t("dashboard:volumeIntensity.title"),
    href: "/dashboard/metrics/volume-intensity",
    iconSrc: "/img/new-icons/volume-intensity-ico.svg",
    iconComponent: (color) => <VolumeIntensityIcon color={color} />,
  },
  {
    name: t("dashboard:baselineVolume.title"),
    href: "/dashboard/metrics/baseline-volume-relative-intensity",
    iconSrc: "/img/new-icons/baseline-vol-rel-intensity-ico.svg",
    iconComponent: (color) => <VolumeIntensityIcon color={color} />,
  },
  {
    name: t("dashboard:overUnderBaseline.title"),
    href: "/dashboard/metrics/over-under-baseline",
    iconSrc: "/img/new-icons/over-under-baseline-vol-ico.svg",
    iconComponent: (color) => <OverUnderBaselineIcon color={color} />,
  },
  {
    name: t("dashboard:volumeSplitCoreLift.title"),
    href: "/dashboard/metrics/volume-split-core-lift",
    iconSrc: "/img/new-icons/volume-split-ico.svg",
    iconComponent: (color) => <VolumeSplitCoreLiftIcon color={color} />,
  },
  {
    name: t("dashboard:recordTable.title"),
    href: "/dashboard/metrics/record-table",
    iconSrc: "/img/new-icons/personal-record-ico.svg",
    iconComponent: (color) => <PersonalRecordTableIcon color={color} />,
  },
  {
    name: t("dashboard:numberOfLifts.title"),
    href: "/dashboard/metrics/number-of-lifts",
    iconSrc: "/img/new-icons/number-of-lifts-ico.svg",
    iconComponent: (color) => <NumberLiftsIcon color={color} />,
  },
  {
    name: t("dashboard:coreAccessoryVolumeSplit.title"),
    href: "/dashboard/metrics/core-accessory-volume-split",
    iconSrc: "/img/new-icons/primary-secondary-vol-split-ico.svg",
    iconComponent: (color) => <PrimarySecondaryVolumeSplitIcon color={color} />,
  },
  {
    name: t("dashboard:repMaxEstimator.title"),
    href: "/dashboard/metrics/rep-max-estimator",
    iconSrc: "/img/new-icons/rep-max-ico.svg",
    iconComponent: (color) => <RepMaxIcon color={color} />,
  },
  {
    name: t("dashboard:metrics.intensityMeasureCustomTables", {
      intensityMeasure,
    }),
    href: "/dashboard/metrics/rpe-table",
    iconSrc: "/img/new-icons/rpe-custom-ico.svg",
    iconComponent: (color) => <RPECustomIcon color={color} />,
  },
  {
    name: t("dashboard:metrics.customMetricReports"),
    href: "/dashboard/metrics/custom",
    iconSrc: "/img/new-icons/custom-metrics-ico.svg",
    className: "custom-metrics",
    iconComponent: (color) => <VolumeIntensityIcon color={color} />,
  },
];

class View extends React.PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      webViewState: null,
    };
    this.backButtonRef = React.createRef();
  }

  componentDidMount() {
    const message = {
      type: "navigate-main",
      message: "Main Metrics View",
    };
    if (window.ReactNativeWebView) {
      if (this.backButtonRef.current) {
        this.backButtonRef.current.style.display = "none";
      }
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
    }
    this.setState({ webViewState: window.ReactNativeWebView });
  }

  componentDidUpdate() {
    if (window.ReactNativeWebView) {
      if (this.backButtonRef.current) {
        this.backButtonRef.current.style.display = "none";
      }
    }
  }

  sendNavigationMessage() {
    const message = {
      type: "navigate",
      message: "Metrics Section",
    };
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
    }
  }

  render() {
    const { locals } = this.context;
    const { activeUser, activeUserToRequest } = locals;
    const intensityMeasure =
      get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
        ? "RPE"
        : "RiR";
    const isAdmin = get(locals, "activeUser.admin");
    const query = get(locals, "query");

    const pathname = get(locals, "pathname");
    const adminOnly = cx("block", { disabled: !isAdmin });
    const { feBranding = {} } = locals;

    const metricsBlocks = getMetricsBlocks({
      intensityMeasure,
      t: this.props.t,
    });
    defaults(feBranding, {
      primaryColor: "transparent",
      secondaryTextColor: "#181818",
      appTheme: "dark",
    });

    const themeStyles = {
      bg: {
        dark: "#181818",
        light: "#F2F2F2",
      },
      text: {
        dark: "rgba(255, 255, 255, 0.87)",
        light: "rgba(0, 0, 0, 0.87)",
      },
      card: {
        dark: "#272727",
        light: "#FFFFFF",
      },
    };
    const style = {
      "--primary-color": feBranding.primaryColor,
      "--secondary-text-color": feBranding.secondaryTextColor,
      "--bg-color": themeStyles.bg[feBranding.appTheme],
      "--card-color": themeStyles.card[feBranding.appTheme],
      "--card-text-color": themeStyles.text[feBranding.appTheme],
    };

    const isMobileWebview = query.randomReload ? true : false;

    return (
      <main
        role="reports-wrapper"
        data-theme={
          isMobileWebview && feBranding.appTheme
            ? feBranding.appTheme
            : undefined
        }
        style={isMobileWebview ? style : undefined}
      >
        <NavigationComponent
          query={query}
          pathName={pathname}
          text={this.props.t("dashboard:metrics.calendarDayView")}
          classNames="mobile-hidden"
        />
        <article role="report-content" className="metrics-dashboard">
          <header ref={this.backButtonRef}>
            <h2 className="mobile-hidden">
              {this.props.t("dashboard:metrics.strengthMetrics")}
            </h2>
            <ReportsBackHandler
              title={this.props.t("dashboard:metrics.strengthMetrics")}
              locals={locals}
            />
          </header>
          <section className="Metrics-Blocks">
            {metricsBlocks.map(
              ({ href, iconSrc, name, className, iconComponent }) => {
                return (
                  <div
                    key={name}
                    className={`block${className ? ` ${className}` : ""}`}
                  >
                    <a
                      href={isMobileWebview ? `${href}?mobileWV=true` : href}
                      onClick={() => this.sendNavigationMessage()}
                    >
                      <div>
                        {isMobileWebview ? (
                          <picture>
                            {iconComponent(feBranding.secondaryTextColor)}
                          </picture>
                        ) : (
                          <SvgIcon src={iconSrc} classNames="metrics-icon" />
                        )}
                        <span className="name">{name}</span>
                      </div>
                      <picture className="only-show-mobile">
                        <img src="/img/new-icons/arrow-right.svg" />
                      </picture>
                    </a>
                  </div>
                );
              }
            )}
          </section>
        </article>
      </main>
    );
  }
}

View.contextType = RillContext;

function MetricsViewWrapper() {
  const { t } = useTranslation("dashboard");
  return <View t={t} />;
}

const Metrics = (app) => {
  app.get("/", async ({ req, res, load, locals }) => {
    const { activeUser: user } = locals;
    if (isPushOrPerformClient(user) || user.coach) {
      await load("feBranding", {});
    }

    res.body = <MetricsViewWrapper />;
  });
};

export default Metrics;
